import { useEffect, useMemo, useState } from "react";
import { addDays, format } from "date-fns";
import { supabase } from "@/lib/supabase";
import { formatDate } from "@/lib/dates";
import { loadVehicleTypes, getIconStyle } from "@/lib/vehicleTypes";

export interface DayOption {
  date: Date;
  dateStr: string;
  label: string;
}

export const buildDayOptions = (): DayOption[] => {
  const base = new Date();
  return [0, 1, 2].map((offset) => {
    const d = addDays(base, offset);
    return {
      date: d,
      dateStr: format(d, "yyyy-MM-dd"),
      label: offset === 0 ? "Today" : offset === 1 ? "Tomorrow" : `${format(d, "EEE")} ${formatDate(d)}`,
    };
  });
};

/**
 * Which of the TMV / Car-SUV / STS driver slot tabs should show for this
 * driver -- gated on whether they have a REAL driving shift (not Leave/
 * Sick/Training, which still carry a vehicle_type but aren't a real
 * capacity slot -- see fetchShiftTypeCodesForVehicleType in
 * fleet-capacity/utils.ts) in that segment on today/tomorrow/day-after.
 *
 * TMV uses requires_tmv (a precomputed view flag). STS deliberately does
 * NOT use requires_sts -- that flag also covers an unrelated DMD loan-shift
 * type -- it uses the same "shift_type_code starts with STS_" signal Fleet
 * Capacity's own STS tab uses (see fetchShiftTypeCodesByPrefix in
 * fleet-capacity/utils.ts). Car/SUV has no
 * dedicated flag at all, so it's computed from
 * roster_shift_types.allowed_vehicle_types: a shift type counts if its
 * allowed list is exactly one vehicle type whose icon_style is "car".
 */
export function useDriverFleetWindow(driverName: string) {
  const dayOptions = useMemo(buildDayOptions, []);
  const [tmvDateStrs, setTmvDateStrs] = useState<Set<string>>(new Set());
  const [carDateStrs, setCarDateStrs] = useState<Set<string>>(new Set());
  const [stsDateStrs, setStsDateStrs] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!driverName) return;
    let cancelled = false;
    (async () => {
      const [shiftsRes, shiftTypesRes, vehicleTypes] = await Promise.all([
        supabase
          .from("vw_roster_shifts_enriched")
          .select("shift_date, requires_tmv, shift_type_code")
          .eq("driver_name", driverName)
          .in("shift_date", dayOptions.map((d) => d.dateStr)),
        supabase.from("roster_shift_types").select("code, allowed_vehicle_types"),
        loadVehicleTypes(),
      ]);
      if (cancelled) return;

      const rows = (shiftsRes.data || []) as { shift_date: string; requires_tmv: boolean | null; shift_type_code: string | null }[];
      const carCodes = new Set(
        ((shiftTypesRes.data || []) as { code: string; allowed_vehicle_types: string[] | null }[])
          .filter(
            (r) =>
              Array.isArray(r.allowed_vehicle_types) &&
              r.allowed_vehicle_types.length === 1 &&
              getIconStyle(r.allowed_vehicle_types[0], vehicleTypes) === "car"
          )
          .map((r) => r.code)
      );

      setTmvDateStrs(new Set(rows.filter((r) => r.requires_tmv).map((r) => r.shift_date)));
      setCarDateStrs(new Set(rows.filter((r) => r.shift_type_code && carCodes.has(r.shift_type_code)).map((r) => r.shift_date)));
      setStsDateStrs(new Set(rows.filter((r) => (r.shift_type_code || "").startsWith("STS_")).map((r) => r.shift_date)));
    })();
    return () => {
      cancelled = true;
    };
  }, [driverName, dayOptions]);

  return {
    dayOptions,
    tmvDateStrs,
    carDateStrs,
    stsDateStrs,
    anyTmv: tmvDateStrs.size > 0,
    anyCar: carDateStrs.size > 0,
    anySts: stsDateStrs.size > 0,
  };
}
